import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslation } from 'react-i18next';

import Chip from '@/components/atoms/chip';
import { Label } from '@/components/atoms/label';
import SettingsModal from '@/components/molecules/Modals/SettingsModal';
import { capitalizeFirstLetter } from '@/helpers/filterNames';

interface TranscriptInfoModalProps {
  isOpen: boolean;
  onClose: () => void;
  transcriptName: string;
  transcriptCreatedDate: Date | string | null;
  transcriptDeponent: string | null;
  transcriptObjective: string | null;
  transcriptOtherSpeakers: string[] | null;
  transcriptPhraseList: string[] | null;
}

export const TranscriptInfoModal = ({
  isOpen,
  onClose,
  transcriptName,
  transcriptCreatedDate,
  transcriptDeponent,
  transcriptObjective,
  transcriptOtherSpeakers,
  transcriptPhraseList,
}: TranscriptInfoModalProps) => {
  const { t } = useTranslation();

  const formatDate = (date: Date | string | null) => {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const modalContent = (
    <div className="px-6 py-4">
      <div className="flex flex-col gap-4 text-sm">
        {/* Basic Information */}
        <div>
          <div className="mt-2 flex flex-col gap-2">
            <div className="flex justify-between">
              <Label>Name:</Label>
              <span className="text-gray-600">{transcriptName}</span>
            </div>

            <div className="flex justify-between">
              <Label>Created:</Label>
              <span className="text-gray-600">{formatDate(transcriptCreatedDate)}</span>
            </div>
          </div>
        </div>

        {(transcriptDeponent || transcriptObjective || transcriptOtherSpeakers?.length) && (
          <div>
            <div className="mt-2 flex flex-col gap-4">
              {transcriptDeponent && (
                <div className="flex justify-between">
                  <Label>{capitalizeFirstLetter(t('witness'))}:</Label>
                  <span className="text-gray-600">{transcriptDeponent}</span>
                </div>
              )}
              {transcriptObjective && (
                <div className="flex flex-col gap-2">
                  <Label>Objective:</Label>
                  <span className="text-left text-sm text-gray-600">{transcriptObjective}</span>
                </div>
              )}
              {transcriptOtherSpeakers && transcriptOtherSpeakers.length > 0 && (
                <div className="flex flex-col gap-2">
                  <Label>Other Speakers:</Label>
                  <div className="flex flex-wrap gap-1">
                    {transcriptOtherSpeakers.map((speaker, index) => (
                      <Chip key={index} className="bg-blue-100 text-blue-800">
                        {speaker}
                      </Chip>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Phrase List */}
        {transcriptPhraseList && transcriptPhraseList.length > 0 && (
          <div className="flex flex-col gap-2">
            <Label>Phrase List:</Label>
            <div className="flex flex-wrap gap-1">
              {transcriptPhraseList.map((phrase, index) => (
                <Chip key={index} className="bg-gray-100 text-gray-800">
                  {phrase}
                </Chip>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <SettingsModal
      title={
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faInfoCircle} className="text-brandSecondary h-5 w-5" />
          <span>Transcript Information</span>
        </div>
      }
      content={modalContent}
      isOpen={isOpen}
      handleClose={onClose}
      size="small"
    />
  );
};
